import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Download } from '@/lib/icons';
import {
  Header,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Spinner,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useReportFilters } from '@/hooks/use-report-filters';
import type {
  ReportFilters,
  ReportResponse,
  ReportPageLayoutProps,
  AdminLevel,
} from '@/types/reports';
import { ErrorBoundary } from './error-boundary';
import { ReportFiltersBar } from './report-filters';
import { ReportPagination } from './report-pagination';
import {
  ReportLoadingState,
  ReportErrorState,
  ReportEmptyState,
  ReportAccessDeniedState,
} from './report-states';

type ExportFormat = NonNullable<ReportFilters['export_format']>;

const DOWNLOAD_OPTIONS = [
  { format: 'xlsx', label: 'Download Excel', variant: 'lilac' },
  { format: 'csv', label: 'Download CSV', variant: 'outline' },
] as const satisfies ReadonlyArray<{ format: ExportFormat; label: string; variant: string }>;

/**
 * Main layout component for report pages
 * Handles data fetching, filtering, pagination, and display
 */
export function ReportPageLayout<TData>({
  title,
  description,
  breadcrumbLabel,
  itemLabel,
  queryKey,
  fetchData,
  downloadData,
  filterConfig,
  columns,
  sortData,
  getRowKey,
  accessCheck,
  emptyIcon,
  emptyTitle,
  emptyDescription,
}: ReportPageLayoutProps<TData>) {
  usePageContext({ showPageHeader: false });
  const { data: currentAdmin } = useCurrentUser();
  // For license admins, use 'license_admin' as the level for filter visibility
  // For prosci admins, use their actual level
  const adminLevel: AdminLevel | undefined = currentAdmin?.type === 'license_admin'
    ? 'license_admin'
    : currentAdmin?.level as AdminLevel | undefined;

  const {
    filters,
    currentPage,
    pageSize,
    updateFilter,
    updateDateFilter,
    clearFilters,
    setCurrentPage,
    hasFilters,
    isAffiliate,
    affiliateFilterReady,
  } = useReportFilters({ currentAdmin });

  const [downloadingFormat, setDownloadingFormat] = useState<ExportFormat | null>(null);

  // Check access if required
  const hasAccess = useMemo(() => {
    if (!accessCheck) return true;
    if (!adminLevel) return false;
    return accessCheck.requiredLevels.includes(adminLevel);
  }, [accessCheck, adminLevel]);

  // Fetch report data
  const { data, isLoading, error } = useQuery<ReportResponse<TData>>({
    queryKey: [queryKey, filters],
    queryFn: () => fetchData(filters) as Promise<ReportResponse<TData>>,
    enabled: hasAccess && affiliateFilterReady,
  });

  // Sort and paginate data
  const sortedAndPaginatedData = useMemo(() => {
    if (!data?.data) return { items: [], totalPages: 0 };

    const sortedItems = sortData(data.data);
    const totalPages = Math.ceil(sortedItems.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const items = sortedItems.slice(startIndex, endIndex);

    return { items, totalPages };
  }, [data?.data, currentPage, pageSize, sortData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, setCurrentPage]);

  const handleDownload = async (exportFormat: ExportFormat) => {
    setDownloadingFormat(exportFormat);
    try {
      await downloadData({ ...filters, export_format: exportFormat });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to download report. Please try again.');
    } finally {
      setDownloadingFormat(null);
    }
  };

  // Render column header
  const renderHeader = (
    header: string | ((adminLevel?: AdminLevel) => string)
  ): string => {
    if (typeof header === 'function') {
      return header(adminLevel);
    }
    return header;
  };

  return (
    <ErrorBoundary>
      <Header
        title={title}
        size="small"
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/reports">Reports</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{breadcrumbLabel}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      />

      <ReportFiltersBar
        filters={filters}
        filterConfig={filterConfig}
        adminLevel={adminLevel}
        isAffiliate={isAffiliate}
        onFilterChange={updateFilter}
        onDateChange={updateDateFilter}
        onClear={clearFilters}
        hasFilters={hasFilters}
      />

      <ContentContainer>
        {!hasAccess ? (
          <ReportAccessDeniedState message={accessCheck?.deniedMessage} />
        ) : error ? (
          <ReportErrorState error={error} />
        ) : isLoading && !data ? (
          <ReportLoadingState title={`Loading ${itemLabel}`} />
        ) : data && data.data?.length === 0 ? (
          <ReportEmptyState
            icon={emptyIcon}
            title={emptyTitle || `No ${itemLabel} found`}
            description={
              emptyDescription ||
              `Try adjusting your filters to find ${itemLabel}.`
            }
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
          />
        ) : data ? (
          <Card>
            <CardHeader>
              <CardTitle>{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
              <CardAction className="flex gap-2">
                {DOWNLOAD_OPTIONS.map(({ format, label, variant }) => (
                  <Button
                    key={format}
                    type="button"
                    onClick={() => handleDownload(format)}
                    disabled={downloadingFormat !== null}
                    variant={variant}
                  >
                    {downloadingFormat === format ? (
                      <Spinner size="small" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    {label}
                  </Button>
                ))}
              </CardAction>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((column) => (
                      <TableHead key={column.key} className={column.className}>
                        {renderHeader(column.header)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAndPaginatedData.items.map((item) => (
                    <TableRow key={getRowKey(item)}>
                      {columns.map((column) => (
                        <TableCell key={column.key} className={column.className}>
                          {column.render(item, adminLevel)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <ReportPagination
                currentPage={currentPage}
                totalPages={sortedAndPaginatedData.totalPages}
                pageSize={pageSize}
                totalCount={data.meta?.total_count || 0}
                itemLabel={itemLabel}
                onPageChange={setCurrentPage}
              />
            </CardContent>
          </Card>
        ) : null}
      </ContentContainer>
    </ErrorBoundary>
  );
}
